import React, { useEffect, useState } from "react";
import { hideHeader, changeBgColor, hideLogoAndFooter } from "../../../actions/appStatus.actions";
import {
  callAuthGetLoans,
  clearAuthLoans,
  callCreateAuthPaymentRecord,
  callAuthGeneratePayoff,
  clearAuthRecord,
  resetAuthFlow,
  setAuthRecordCreated,
} from "../../../actions/authPayment.actions";
import { clearAchInstitution } from "../../../actions/achInstitution.actions";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { destroy } from "redux-form";
import AuthPaymentModal from "../modal/auth-payment-modal.component";
import { AuthPaymentLoanList } from "../loan-list/auth-payment-loan-list.component";
import { AuthPaymentErrorPage } from "../error-page/auth-payment-error-page.component";
import StandardDebitCardFlow from "../flow-components/standard-debit-flow.component";
import StandardExternalAchFlow from "../flow-components/standard-external-ach-flow.component";
import StandardInternalTransferFlow from "../flow-components/standard-internal-transfer-flow.component";
import RecurringAchClosedEndFlow from "../flow-components/recurring-ach-closed-end-flow.component";
import RecurringAchOpenEndFlow from "../flow-components/recurring-ach-open-end-flow.component";
import Show from "../../../utils/show.component";
import RecurringCancellationFlow from "../flow-components/recurring-cancellation-flow.component";
import { authPaymentTypes } from "./data/auth-payment-types";
import SkipAPayFlow from "../flow-components/skip-a-pay-flow.component";
import { isAmountPastDueBetweenBalanceAndPayoff } from "../../../utils/utils";
import { clearMfa } from "../../../actions/mfa.actions";
import RecurringInternalTransferOpenEndFlow from "../flow-components/recurring-internal-transfer-open-end-flow.component";
import RecurringInternalTransferClosedEndFlow from "../flow-components/recurring-internal-transfer-closed-end-flow.component";

const AuthPaymentParentComponent = (props) => {
  // Used to determine modal content
  const initialModalState = {
    show: false,
    paymentType: null,
    selectedLoanId: null,
    achIsAllowed: null,
    isOpenEnded: null,
    isPrincipalOnly: null,
    hasSelectedPayoff: null,
  };

  // Set after modal selections and determines flow components
  const initialPaymentInfoState = {
    method: null,
    type: null,
    loanId: null,
    isOpenEnded: null,
    isPrincipalOnly: null,
    // This property is only for the pre-determined payoff flow
    // When the user selects Payoff from the popup menu
    hasSelectedPayoff: null,
  };

  const loans = props.authPayment.loans;
  const [modalState, setModalState] = useState(initialModalState);
  const [processStep, setProcessStep] = useState(0);
  const [componentInitialized, setComponentInitialized] = useState(false);

  // Payment info gets set after a loan card payment button has
  // been pressed and a payment type is selected from the modal
  const [paymentInfo, setPaymentInfo] = useState(initialPaymentInfoState);

  const loanSelector =
    Array.isArray(loans) && loans.filter((loan) => loan.AccountHash === paymentInfo.loanId);
  const selectedLoan = loanSelector.length ? loanSelector[0] : null;

  let amountPastDueBetweenPayoffAndBalance = false;
  if (selectedLoan) {
    amountPastDueBetweenPayoffAndBalance = isAmountPastDueBetweenBalanceAndPayoff(
      selectedLoan.AmountPastDue,
      selectedLoan.PayoffAmount,
      selectedLoan.CurrentBalance
    );
  }

  // Get the loan from modalState to pass into the modal
  // Because this is before paymentInfo is set
  const getModalStateLoan = () => {
    const selected = loans.filter((loan) => loan.AccountHash === modalState.selectedLoanId);

    if (selected.length) {
      return selected[0];
    }

    return {};
  };

  const showAuthPaymentModal = (
    show,
    paymentType,
    selectedLoanId,
    achIsAllowed,
    isOpenEnded,
    isPrincipalOnly,
    hasSelectedPayoff
  ) => {
    setModalState({
      show: show,
      paymentType: paymentType,
      selectedLoanId: selectedLoanId,
      achIsAllowed: achIsAllowed,
      isOpenEnded: isOpenEnded,
      isPrincipalOnly: isPrincipalOnly,
      hasSelectedPayoff: hasSelectedPayoff,
    });
  };

  useEffect(() => {
    if (!componentInitialized) {
      setComponentInitialized(true);
    }

    props.hideLogoAndFooter();
    props.hideHeader();
    props.changeBgColor("#fcfdfe");
  }, []);

  useEffect(() => {
    // After the selected loan has changed by choosing a loan and payment type in the modal,
    // send a call to CreatePersonRecord with the selected loan number
    if (selectedLoan) {
      // Normal payment flows will call CreatePersonRecord
      // Skip a Pay flows will be called from the popup menu and call GetSkipInfo, so can be omitted here
      // In both cases, we set recordCreated to true to trigger the next step so we always need to clear it
      props.clearAuthRecord();

      // These types do not need to call CreatePersonRecord here
      const createPaymentRecordExceptions = [
        authPaymentTypes.valid.EligibleSkip,
        authPaymentTypes.other.EligibleTravelExemption,
      ];

      if (!createPaymentRecordExceptions.includes(paymentInfo.type)) {
        props.callCreateAuthPaymentRecord(selectedLoan.AccountHash, loans.indexOf(selectedLoan));
      }
    }
  }, [selectedLoan]);

  useEffect(() => {
    // Destroy all form state and payment info if we are at the initial step
    if (processStep === 0) {
      props.clearMfa();
      props.resetAuthFlow();

      setPaymentInfo(initialPaymentInfoState);
      setModalState(initialModalState);

      props.clearAchInstitution();

      // Destroy all form state
      Object.keys(props.form).forEach((form) => {
        props.destroy(form);
      });

      props.callAuthGetLoans();
    }
  }, [processStep]);

  useEffect(() => {
    if (props.authPayment.recordCreated && componentInitialized) {
      setProcessStep(1);
    }
  }, [props.authPayment.recordCreated]);

  const backToLoans = () => {
    window.scrollTo(0, 0);
    props.resetAuthFlow();
    setProcessStep(0);
  };

  // Determines which component to display depending on which
  // flow is selected
  const determineComponent = (paymentInfo) => {
    if (paymentInfo.type === authPaymentTypes.valid.Standard) {
      switch (paymentInfo.method) {
        case "DebitCard":
          return StandardDebitCardFlow;
        case "BankAccount":
          return StandardExternalAchFlow;
        case "LakeTrustAccount":
          return StandardInternalTransferFlow;
        default:
          return () => <div>Something went wrong.</div>;
      }
    }

    if (paymentInfo.type === authPaymentTypes.valid.Recurring) {
      switch (paymentInfo.method) {
        case "BankAccount":
          if (!paymentInfo.isOpenEnded) {
            return RecurringAchClosedEndFlow;
          }
          if (paymentInfo.isOpenEnded) {
            return RecurringAchOpenEndFlow;
          }
        case "LakeTrustAccount":
          if (!paymentInfo.isOpenEnded) {
            return RecurringInternalTransferClosedEndFlow;
          }
          if (paymentInfo.isOpenEnded) {
            return RecurringInternalTransferOpenEndFlow;
          }
        default:
          return () => <div>Something went wrong.</div>;
      }
    }

    if (paymentInfo.type === authPaymentTypes.valid.RecurringCancellation) {
      return RecurringCancellationFlow;
    }

    if (paymentInfo.type === authPaymentTypes.valid.EligibleSkip) {
      return SkipAPayFlow;
    }
  };

  const SelectedFlowComponent = determineComponent(paymentInfo);

  if (props.authPayment.hasError) {
    return <AuthPaymentErrorPage />;
  }

  return (
    <div>
      <AuthPaymentModal
        {...modalState}
        hideModal={() => setModalState(initialModalState)}
        setProcessStep={setProcessStep}
        setPaymentInfo={setPaymentInfo}
        setAuthRecordCreated={props.setAuthRecordCreated}
        callAuthGeneratePayoff={props.callAuthGeneratePayoff}
        authPayment={props.authPayment}
        loan={getModalStateLoan()}
        memberFromDI={props.authPayment.memberFromDI}
      />
      <Show condition={processStep === 0}>
        <AuthPaymentLoanList
          showAuthPaymentModal={showAuthPaymentModal}
          setProcessStep={setProcessStep}
          setPaymentInfo={setPaymentInfo}
          callAuthGeneratePayoff={props.callAuthGeneratePayoff}
          loansLoaded={props.authPayment.loansLoaded}
          authPayment={props.authPayment}
          loansArray={loans}
          memberFromDI={props.authPayment.memberFromDI}
        />
      </Show>
      <Show condition={processStep !== 0}>
        <SelectedFlowComponent
          paymentInfo={paymentInfo}
          setProcessStep={setProcessStep}
          processStep={processStep}
          selectedLoan={selectedLoan}
          backToLoans={backToLoans}
          amountPastDueBetweenPayoffAndBalance={amountPastDueBetweenPayoffAndBalance}
          {...props}
        />
      </Show>
    </div>
  );
};

// Map global store to component props
const mapStateToProps = (state) => state;

// Hook up actions to component
const mapDispatchToProps = {
  hideHeader,
  changeBgColor,
  hideLogoAndFooter,
  setAuthRecordCreated,
  callAuthGetLoans,
  callCreateAuthPaymentRecord,
  clearAchInstitution,
  callAuthGeneratePayoff,
  clearAuthLoans,
  clearAuthRecord,
  clearMfa,
  resetAuthFlow,
  destroy,
};

//Connect to Redux store
const AuthPaymentParent = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AuthPaymentParentComponent)
);

export default AuthPaymentParent;



// WEBPACK FOOTER //
// ./src/components/authenticated-payments/parent/auth-payment-parent.component.js